// src/services/queries/signals/signalQueries.ts
// API query functions for signal snapshot series

import { queryOptions } from "@tanstack/react-query";
import { API_BASE_URL } from "@/services";
import { signalQueryKeys } from "@/constants/queryKeys";
// import { SIGNAL_RUN_ID } from "@/constants/signals";
import type { SignalSeriesResponse } from "@/types/signals";

export interface SignalSeriesParams {
  runId?: string;
  signalName: string;
  rawFeature?: string;
  startTs?: string;
  endTs?: string;
}

/**
 * Fetch signal time series from the backend.
 * GET /api/v1/signal-snapshots/{run_id}/series?signal_name=...
 */
export async function fetchSignalSeries(
  params: SignalSeriesParams,
): Promise<SignalSeriesResponse> {
  // const runId = params.runId ?? SIGNAL_RUN_ID;
  const searchParams = new URLSearchParams();

  searchParams.append("signal_name", params.signalName);
  if (params.rawFeature) searchParams.append("raw_feature", params.rawFeature);
  if (params.startTs) searchParams.append("start_ts", params.startTs);
  if (params.endTs) searchParams.append("end_ts", params.endTs);

  const url = `${API_BASE_URL}/signal-snapshots/d8391d38-6495-4837-b974-d49c41a05ea5/series?${searchParams.toString()}`;
  const response = await fetch(url);

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ detail: "Unknown error" }));
    throw new Error(
      error.detail || `Failed to fetch signal series for ${params.signalName}`,
    );
  }

  return response.json();
}

/**
 * React Query options factory for signal series data.
 * 5-minute stale time to match backend refresh cadence.
 */
export function signalSeriesQueryOptions(params: SignalSeriesParams) {
  const keyParams: {
    runId: string;
    signalName: string;
    rawFeature?: string;
    startTs?: string;
    endTs?: string;
  } = {
    runId: "d8391d38-6495-4837-b974-d49c41a05ea5",
    signalName: params.signalName,
  };
  if (params.rawFeature != null) keyParams.rawFeature = params.rawFeature;
  if (params.startTs != null) keyParams.startTs = params.startTs;
  if (params.endTs != null) keyParams.endTs = params.endTs;

  return queryOptions({
    queryKey: signalQueryKeys.series(keyParams),
    queryFn: () => fetchSignalSeries(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}
